/**
 * Dependency-free word-level diff for the Version History panel (Pro).
 *
 * Tokens are whitespace-split words plus a paragraph-break sentinel. The two
 * token streams are aligned with a classic LCS over the changed middle only
 * (common prefix/suffix trimmed first, which collapses most real version
 * diffs to a few changed sections). If the remaining middle would need too
 * large a DP table, it degrades to one removed block + one added block —
 * always correct, just coarser.
 */

export type DiffOp = 'same' | 'added' | 'removed';

export interface DiffSegment {
  op: DiffOp;
  /** One or more consecutive words sharing the same op, joined with spaces. */
  text: string;
}

export interface WordDiff {
  /** Paragraphs in merged (old ∪ new) order; each a run-length segment list. */
  paragraphs: DiffSegment[][];
  /** Word counts (paragraph breaks excluded). */
  added: number;
  removed: number;
}

/** Paragraph-break sentinel — contains \n so it can't collide with a word. */
const PARA = '\n¶';

/** ≈6 MB Uint32 DP table at most; beyond this fall back to block replace. */
const MAX_CELLS = 1_500_000;

interface TokenOp {
  op: DiffOp;
  token: string;
}

function tokenize(text: string): string[] {
  const out: string[] = [];
  for (const chunk of text.replace(/\r\n?/g, '\n').split(/(\n+)/)) {
    if (!chunk) continue;
    if (chunk.charCodeAt(0) === 10 /* \n */) {
      out.push(PARA);
    } else {
      for (const word of chunk.split(/\s+/)) {
        if (word) out.push(word);
      }
    }
  }
  return out;
}

function pushAll(ops: TokenOp[], tokens: readonly string[], op: DiffOp): void {
  for (const token of tokens) ops.push({ op, token });
}

/** Standard LCS dynamic program with backtrack — only ever sees the middle. */
function lcsOps(a: readonly string[], b: readonly string[]): TokenOp[] {
  const n = a.length;
  const m = b.length;
  const w = m + 1;
  const dp = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * w + j] =
        a[i] === b[j]
          ? (dp[(i + 1) * w + j + 1] ?? 0) + 1
          : Math.max(dp[(i + 1) * w + j] ?? 0, dp[i * w + j + 1] ?? 0);
    }
  }
  const ops: TokenOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const ai = a[i] as string;
    const bj = b[j] as string;
    if (ai === bj) {
      ops.push({ op: 'same', token: ai });
      i++;
      j++;
    } else if ((dp[(i + 1) * w + j] ?? 0) >= (dp[i * w + j + 1] ?? 0)) {
      ops.push({ op: 'removed', token: ai });
      i++;
    } else {
      ops.push({ op: 'added', token: bj });
      j++;
    }
  }
  while (i < n) ops.push({ op: 'removed', token: a[i++] as string });
  while (j < m) ops.push({ op: 'added', token: b[j++] as string });
  return ops;
}

function diffTokens(a: readonly string[], b: readonly string[]): TokenOp[] {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }

  const ops: TokenOp[] = [];
  pushAll(ops, a.slice(0, start), 'same');

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  if (midA.length === 0) {
    pushAll(ops, midB, 'added');
  } else if (midB.length === 0) {
    pushAll(ops, midA, 'removed');
  } else if ((midA.length + 1) * (midB.length + 1) > MAX_CELLS) {
    pushAll(ops, midA, 'removed');
    pushAll(ops, midB, 'added');
  } else {
    for (const op of lcsOps(midA, midB)) ops.push(op);
  }

  pushAll(ops, a.slice(endA), 'same');
  return ops;
}

/** Diff two version texts (old → new) into render-ready paragraphs. */
export function diffWords(oldText: string, newText: string): WordDiff {
  const ops = diffTokens(tokenize(oldText), tokenize(newText));
  const paragraphs: DiffSegment[][] = [];
  let current: DiffSegment[] = [];
  let added = 0;
  let removed = 0;
  for (const { op, token } of ops) {
    if (token === PARA) {
      if (current.length > 0) {
        paragraphs.push(current);
        current = [];
      }
      continue;
    }
    if (op === 'added') added++;
    else if (op === 'removed') removed++;
    const last = current[current.length - 1];
    if (last && last.op === op) {
      last.text += ` ${token}`;
    } else {
      current.push({ op, text: token });
    }
  }
  if (current.length > 0) paragraphs.push(current);
  return { paragraphs, added, removed };
}
